import { Trans } from '@lingui/react/macro';

import { DefectsList } from '@/components/DefectsList';
import Box from '@/design_system/Box';
import FoldableSection from '@/design_system/FoldableSection';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import {
  ConditionQuestion,
  ConditionQuestionError,
} from '@/routes/Requests/New/components/ConditionForm/ConditionQuestion';

import { AssociatedDefects } from './AssociatedDefects';
import { ConditionContextAndPhotos } from './ConditionContextAndPhotos';
import { ConditionQuestionReadonlyAnswers } from './ConditionQuestionReadonlyAnswers';

export const InitialConditionForm = () => {
  const { article, view, errors, initialConditionFormData } = useArticleContext();

  if (!view.initialCondition.shown || !initialConditionFormData) {
    return null;
  }

  const { conditionForm, articleConditionForms } = initialConditionFormData;

  const hasError =
    errors.condition?.hasError || (view.initialCondition.editable && errors?.defects?.hasError);

  return (
    <FoldableSection
      label={
        <Title as="h2">
          <Trans id="requests.new.articles.drawer.conditions-form.title">Initial condition</Trans>
        </Title>
      }
      key={`${article?.step?.step}-${article?.taskType}`}
      startClosed={article.step?.step !== 'creation'}
      isInvalid={hasError}
    >
      <Box padding="1rem" gap="1rem" className="bg-neutral-0">
        <Stack gap="2rem">
          <Stack gap="1rem">
            {conditionForm.questions.map((question) => (
              <Stack key={question.id} gap="0.25rem">
                <Box padding="0rem">
                  <ConditionQuestion
                    question={question}
                    type="initial-condition"
                    articleConditionForms={articleConditionForms}
                  />
                  <ConditionQuestionReadonlyAnswers
                    question={question}
                    articleConditionForms={articleConditionForms}
                  />
                  <ConditionContextAndPhotos
                    question={question}
                    type="initial-condition"
                    articleConditionForms={articleConditionForms}
                  />
                  {view.initialCondition.editable && <AssociatedDefects question={question} />}
                </Box>
                <ConditionQuestionError question={question} type="initial-condition" />
              </Stack>
            ))}
          </Stack>
          {view.initialCondition.editable && (
            <Stack gap="0.25rem">
              <p className="label-100-medium text-primary">
                <Trans id="defects-table.title.label-additional">Additional defects</Trans>
              </p>
              <DefectsList showOnlyDefectsNotAssociatedToInitialCondition />
            </Stack>
          )}
        </Stack>
      </Box>
    </FoldableSection>
  );
};
